import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import type { Tenant, TenantOverviewDTO } from '../../../types/lac';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../../components/liks/LiksDropdown/LiksDropdown';
import { LiksSearch } from '../../../components/liks/LiksSearch/LiksSearch';
import { useCreateLacTenantMutation } from '../../../features/lac/mutations/useLacTenantMutations';
import { useLacCostLimitsQuery } from '../../../features/lac/queries/useLacCostLimitQuery';
import { useLacTenantOverviewsQuery } from '../../../features/lac/queries/useLacTenantOverviewsQuery';
import { useLacTenantsQuery } from '../../../features/lac/queries/useLacTenantsQuery';
import {
  getCurrentMonthDateRange,
  getCurrentYearDateRange,
} from '../../../features/lac/tenantOverviewUtils';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { LacCreateTenantInput } from '../../../types/lac';
import { CreateLacTenantDialog } from '../CreateLacTenantDialog';
import { LacOverallCostsCard } from '../LacOverallCostsCard';
import { LacTenantRow } from '../LacTenantRow';
import './LacTenantsTab.css';

type StatusFilter = 'all' | 'active' | 'inactive';

function tenantDisplayName(tenant: Tenant): string {
  return tenant.displayName?.trim() || tenant.name;
}

function matchesStatusFilter(
  tenant: Tenant,
  statusFilter: StatusFilter,
): boolean {
  if (statusFilter === 'all') {
    return true;
  }
  if (tenant.active == null) {
    return false;
  }
  return statusFilter === 'active' ? tenant.active : !tenant.active;
}

function matchesSearch(tenant: Tenant, query: string): boolean {
  if (query === '') {
    return true;
  }

  return (
    tenantDisplayName(tenant).toLowerCase().includes(query) ||
    tenant.name.toLowerCase().includes(query) ||
    (tenant.description?.toLowerCase().includes(query) ?? false)
  );
}

export function LacTenantsTab() {
  const { translate } = useI18n();
  const tenantsQuery = useLacTenantsQuery();
  const monthRange = useMemo(() => getCurrentMonthDateRange(), []);
  const yearRange = useMemo(() => getCurrentYearDateRange(), []);
  // All-time for row metrics (users / KDBs / chatbots / crawls); month+year only for costs.
  const overviewsQuery = useLacTenantOverviewsQuery();
  const monthlyOverviewsQuery = useLacTenantOverviewsQuery(monthRange);
  const yearlyOverviewsQuery = useLacTenantOverviewsQuery(yearRange);
  const costLimitsQuery = useLacCostLimitsQuery();
  const createTenantMutation = useCreateLacTenantMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const isLoading = tenantsQuery.isLoading;
  const isError = tenantsQuery.isError;

  const monthlyCostsByTenant = useMemo(() => {
    const map = new Map<string, number>();
    for (const overview of monthlyOverviewsQuery.data ?? []) {
      const tenantName = overview.tenantName?.trim();
      if (!tenantName) {
        continue;
      }
      const estimatedCosts = overview.tokenData?.estimatedCosts;
      if (estimatedCosts != null && Number.isFinite(estimatedCosts)) {
        map.set(tenantName, estimatedCosts);
      }
    }
    return map;
  }, [monthlyOverviewsQuery.data]);

  const yearlyCostsByTenant = useMemo(() => {
    const map = new Map<string, number>();
    for (const overview of yearlyOverviewsQuery.data ?? []) {
      const tenantName = overview.tenantName?.trim();
      if (!tenantName) {
        continue;
      }
      const estimatedCosts = overview.tokenData?.estimatedCosts;
      if (estimatedCosts != null && Number.isFinite(estimatedCosts)) {
        map.set(tenantName, estimatedCosts);
      }
    }
    return map;
  }, [yearlyOverviewsQuery.data]);

  const costLimitsByTenantKey = useMemo(() => {
    const map = new Map<
      string,
      { monthlyLimit: number | null; yearlyLimit: number | null }
    >();
    for (const limit of costLimitsQuery.data ?? []) {
      const tenantKey = limit.tenantId?.trim();
      if (!tenantKey) {
        continue;
      }
      const monthlyLimit =
        limit.monthlyLimit != null &&
        Number.isFinite(limit.monthlyLimit) &&
        limit.monthlyLimit > 0
          ? limit.monthlyLimit
          : null;
      const yearlyLimit =
        limit.yearlyLimit != null &&
        Number.isFinite(limit.yearlyLimit) &&
        limit.yearlyLimit > 0
          ? limit.yearlyLimit
          : null;
      if (monthlyLimit == null && yearlyLimit == null) {
        continue;
      }
      map.set(tenantKey, { monthlyLimit, yearlyLimit });
    }
    return map;
  }, [costLimitsQuery.data]);

  const overviewByTenant = useMemo(() => {
    const map = new Map<string, TenantOverviewDTO>();
    for (const overview of overviewsQuery.data ?? []) {
      const tenantName = overview.tenantName?.trim();
      if (!tenantName) {
        continue;
      }
      map.set(tenantName, overview);
    }
    return map;
  }, [overviewsQuery.data]);

  const filteredTenants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return tenants.filter(
      (tenant) =>
        matchesStatusFilter(tenant, statusFilter) &&
        matchesSearch(tenant, query),
    );
  }, [tenants, searchQuery, statusFilter]);

  const handleStatusFilterChange = (value: string) => {
    if (value === 'all' || value === 'active' || value === 'inactive') {
      setStatusFilter(value);
    }
  };

  const handleCreateTenant = (input: LacCreateTenantInput) => {
    createTenantMutation.mutate(input, {
      onSuccess: () => {
        toast.success({
          title: translate('lac.organisations.create.successTitle'),
          description: translate(
            'lac.organisations.create.successDescription',
            {
              name: input.displayName?.trim() || input.name,
            },
          ),
        });
        setIsCreateDialogOpen(false);
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('lac.organisations.create.errorTitle'),
          translate('lac.organisations.create.errorDescription'),
        );
      },
    });
  };

  if (isLoading) {
    return (
      <p className="lac-tenants__status">
        {translate('lac.organisations.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="lac-tenants__status" role="alert">
        {translate('lac.organisations.loadError')}
      </p>
    );
  }

  return (
    <>
      <div className="lac-tenants">
        <div className="lac-tenants__toolbar">
          <LiksSearch
            className="lac-tenants__search"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={translate('lac.organisations.searchPlaceholder')}
            aria-label={translate('lac.organisations.searchLabel')}
            clearLabel={translate('common.search.clear')}
            variant="on-light"
          />
          <LiksDropdown
            className="lac-tenants__status-filter"
            variant="on-light"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            options={[
              {
                value: 'all',
                label: translate('lac.organisations.statusFilter.all'),
              },
              {
                value: 'active',
                label: translate('lac.organisations.active'),
              },
              {
                value: 'inactive',
                label: translate('lac.organisations.inactive'),
              },
            ]}
            aria-label={translate('lac.organisations.statusFilter.label')}
          />
          <LiksButton
            className="lac-tenants__create-button"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus strokeWidth={2} aria-hidden />
            {translate('lac.organisations.create.open')}
          </LiksButton>
        </div>

        <LacOverallCostsCard />

        {tenants.length === 0 ? (
          <p className="lac-tenants__empty">
            {translate('lac.organisations.empty')}
          </p>
        ) : filteredTenants.length === 0 ? (
          <p className="lac-tenants__empty">
            {translate('lac.organisations.noSearchResults')}
          </p>
        ) : (
          <ul
            className="lac-tenants__list liks-scrollbar"
            aria-label={translate('lac.tabs.organisations')}
          >
            {filteredTenants.map((tenant) => {
              const tenantId = tenant.id?.trim();
              const costLimits =
                (tenantId ? costLimitsByTenantKey.get(tenantId) : undefined) ??
                costLimitsByTenantKey.get(tenant.name);

              return (
                <LacTenantRow
                  key={tenant.name}
                  tenant={tenant}
                  overview={overviewByTenant.get(tenant.name) ?? null}
                  monthlyEstimatedCosts={
                    monthlyCostsByTenant.get(tenant.name) ?? null
                  }
                  yearlyEstimatedCosts={
                    yearlyCostsByTenant.get(tenant.name) ?? null
                  }
                  monthlyCostLimit={costLimits?.monthlyLimit ?? null}
                  yearlyCostLimit={costLimits?.yearlyLimit ?? null}
                />
              );
            })}
          </ul>
        )}
      </div>

      {isCreateDialogOpen && (
        <CreateLacTenantDialog
          onCreate={handleCreateTenant}
          onClose={() => setIsCreateDialogOpen(false)}
          isSubmitting={createTenantMutation.isPending}
        />
      )}
    </>
  );
}
